<script setup lang="ts">
import { ref } from 'vue'
import type { InviteInfo, SpaceInfo } from '../api/types'
import { describeError } from '../api/http'
import { useSession } from '../stores/session'
import Modal from './Modal.vue'

const props = defineProps<{ space: SpaceInfo }>()
const emit = defineEmits<{ close: [] }>()
const session = useSession()

const uses = ref<number | null>(1)
const expiry = ref<number | null>(7 * 24 * 3600)
const invite = ref<InviteInfo | null>(null)
const busy = ref(false)
const error = ref<string | null>(null)
const copied = ref(false)

async function create(): Promise<void> {
  busy.value = true
  error.value = null
  try {
    // Via the engine: the link carries the space's name key in its #fragment.
    invite.value = await session.engine!.createInvite(props.space.id, uses.value ?? undefined, expiry.value ?? undefined)
  } catch (e) {
    error.value = describeError(e)
  } finally {
    busy.value = false
  }
}

async function copy(): Promise<void> {
  if (!invite.value) return
  try {
    await navigator.clipboard.writeText(invite.value.url)
    copied.value = true
    setTimeout(() => (copied.value = false), 2000)
  } catch {
    // Clipboard blocked (e.g. insecure context): the link is selectable.
  }
}
</script>

<template>
  <Modal :title="`Invite friends to ${space.name}`" @close="emit('close')">
    <div v-if="!invite" class="space-y-4">
      <p class="text-sm text-text-muted">
        Anyone with the link can join this space. New people pick a name and they're in — no passwords.
      </p>
      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="label" for="uses">Uses</label>
          <select id="uses" v-model="uses" class="input">
            <option :value="1">1 person</option>
            <option :value="5">5 people</option>
            <option :value="25">25 people</option>
            <option :value="null">Unlimited</option>
          </select>
        </div>
        <div>
          <label class="label" for="expiry">Expires</label>
          <select id="expiry" v-model="expiry" class="input">
            <option :value="3600">1 hour</option>
            <option :value="24 * 3600">1 day</option>
            <option :value="7 * 24 * 3600">7 days</option>
            <option :value="null">Never</option>
          </select>
        </div>
      </div>
      <p v-if="error" class="text-sm text-error">{{ error }}</p>
      <button class="btn-primary w-full" :disabled="busy" data-testid="create-invite" @click="create">
        {{ busy ? 'Creating…' : 'Create invite link' }}
      </button>
    </div>
    <div v-else class="space-y-3">
      <label class="label" for="invite-url">Share this link</label>
      <div class="flex gap-2">
        <input id="invite-url" class="input mono" :value="invite.url" readonly data-testid="invite-url" @focus="($event.target as HTMLInputElement).select()" />
        <button class="btn-primary shrink-0" @click="copy">{{ copied ? 'Copied!' : 'Copy' }}</button>
      </div>
      <p class="mono text-text-muted">
        {{ invite.max_uses ? `Works for ${invite.max_uses} ${invite.max_uses === 1 ? 'person' : 'people'}` : 'Unlimited uses' }}
        ·
        {{ invite.expires_at ? `expires ${new Date(invite.expires_at).toLocaleString()}` : 'never expires' }}
      </p>
    </div>
  </Modal>
</template>
